import React, { FC, useState } from 'react';
import { useAppDispatch } from '../../hooks';
import { loginUser, signupUser } from '../../reducers/userReducer';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { TextInput } from '../FormikMuiFields';
import { notify } from '../../reducers/notificationReducer';
import AlertMessage from '../AlertMessage';
import TermsConsent from '../TermsConsent';
import getErrorMsg from '../../utils/getErrorMsg';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

interface MobileAuthModalProps {
  open: boolean;
  onClose: () => void;
}

interface AuthFormValues {
  username: string;
  password: string;
}

const validationSchemaSignup = yup.object({
  username: yup
    .string()
    .required('Required')
    .max(20, 'Must be at most 20 characters')
    .min(3, 'Must be at least 3 characters')
    .matches(/^[a-zA-Z0-9-_]*$/, 'Only alphanumeric characters allowed, no spaces/symbols'),
  password: yup.string().required('Required').min(6, 'Must be at least 6 characters'),
});

const validationSchemaLogin = yup.object({
  username: yup.string().required('Required'),
  password: yup.string().required('Required'),
});

const MobileAuthModal: FC<MobileAuthModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const [authType, setAuthType] = useState<'login' | 'signup'>('login');
  const [agreed, setAgreed] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (values: AuthFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      setSubmitting(true);
      await dispatch(loginUser(values as any));
      dispatch(notify(`Welcome, ${values.username}. You're logged in!`, 'success'));
      onClose();
    } catch (err) {
      setSubmitting(false);
      setError(getErrorMsg(err));
    }
  };

  const handleSignup = async (values: AuthFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      setSubmitting(true);
      await dispatch(signupUser({ ...values, acceptedTerms: agreed } as any));
      dispatch(notify(`Welcome, ${values.username}. You've been successfully registered.`, 'success'));
      onClose();
    } catch (err) {
      setSubmitting(false);
      setError(getErrorMsg(err));
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setAuthType(newValue === 0 ? 'login' : 'signup');
    setError(null);
  };

  const inputSx = { display: 'flex', alignItems: 'flex-end', mb: 2 };
  const inputIconSx = { marginRight: 1 };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // Guide bottom sheet (.sheet): docked to the bottom edge, 28px top corners.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
      PaperProps={{
        sx: {
          borderRadius: 'var(--sh-xl) var(--sh-xl) 0 0',
          m: 0,
          width: '100%',
          maxWidth: '100%',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: '3px',
          backgroundColor: 'var(--g-line-2)',
          mx: 'auto',
          mt: 1.25,
        }}
      />
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
        <Typography variant="h6">
          {authType === 'login' ? 'Login to your account' : 'Create a new account'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Tabs value={authType === 'login' ? 0 : 1} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 2 }}>
          <Tab label="Login" />
          <Tab label="Sign Up" />
        </Tabs>

        <Formik
          validateOnChange
          initialValues={{ username: '', password: '' }}
          onSubmit={authType === 'login' ? handleLogin : handleSignup}
          validationSchema={authType === 'login' ? validationSchemaLogin : validationSchemaSignup}
        >
          {({ isSubmitting }) => (
            <Form>
              <Box sx={inputSx}>
                <PersonIcon sx={inputIconSx} color="primary" />
                <TextInput
                  name="username"
                  type="text"
                  placeholder="Enter username"
                  label="Username"
                  required
                  fullWidth
                />
              </Box>

              <Box sx={inputSx}>
                <LockIcon sx={inputIconSx} color="primary" />
                <TextInput
                  name="password"
                  type={showPass ? 'text' : 'password'}
                  placeholder="Enter password"
                  label="Password"
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPass((prev) => !prev)} size="small">
                          {showPass ? <VisibilityOffIcon color="primary" /> : <VisibilityIcon color="primary" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {authType === 'signup' && (
                <TermsConsent checked={agreed} onChange={setAgreed} onNavigate={onClose} />
              )}

              <Button
                type="submit"
                color="secondary"
                variant="contained"
                fullWidth
                size="large"
                startIcon={authType === 'login' ? <ExitToAppIcon /> : <PersonAddIcon />}
                sx={{ mt: 2 }}
                disabled={isSubmitting || (authType === 'signup' && !agreed)}
              >
                {authType === 'login' ? (isSubmitting ? 'Logging In' : 'Login') : (isSubmitting ? 'Signing Up' : 'Sign Up')}
              </Button>

              <AlertMessage error={error} severity="error" clearError={() => setError(null)} />
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default MobileAuthModal;
